import { useState } from "react";

const compareValues = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1; // nulls always sorted last
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).toLowerCase().localeCompare(String(b).toLowerCase());
};

/**
 * Hook for client-side column sorting on tables.
 * Usage:
 *   const { sortKey, sortDir, toggleSort, sortData } = useSortableTable();
 *   const rows = sortData(filteredRows, {
 *     name: (r) => r.name,
 *     cost: (r) => r.unit_cost,
 *   });
 * Clicking the same column twice toggles asc → desc.
 */
export const useSortableTable = (initialKey = null, initialDir = "asc") => {
  const [sortKey, setSortKey] = useState(initialKey);
  const [sortDir, setSortDir] = useState(initialDir);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortData = (data, accessors) => {
    if (!sortKey || !accessors?.[sortKey] || !Array.isArray(data)) return data;
    const accessor = accessors[sortKey];
    const sorted = [...data].sort((a, b) => compareValues(accessor(a), accessor(b)));
    return sortDir === "desc" ? sorted.reverse() : sorted;
  };

  return { sortKey, sortDir, toggleSort, sortData };
};
